import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
    useForm,
} from 'react-hook-form-mui';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';

import { BusinessInput } from '@barboxx/admin-components';
import {
    CREATE_CUSTOM_DRINK_SCHEMA,
    useBusinessSelector,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { CustomDrink } from '@barboxx/models';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreateCustomDrinkDialog = ({
    onClose,
    onSuccess,
    open,
}: Props) => {
    const notifications = useNotifications();
    const { businessId } = useBusinessSelector();

    const formContext = useForm<CustomDrink.CreateRequest>({
        resolver: yupResolver(CREATE_CUSTOM_DRINK_SCHEMA),
        defaultValues: {
            businessId,
            upCharge: 1,
        },
    });

    const mutateCreateCustomDrink = useMutation({
        mutationKey: ['customDrink'],
        mutationFn: (data: CustomDrink.CreateRequest) =>
            Api.CustomDrinks.CustomDrink.postCustomDrink(data),
    });

    const onSubmit = async (values: CustomDrink.CreateRequest) => {
        return mutateCreateCustomDrink
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created ${values.name}!`, {
                    severity: 'success',
                });
                onClose();
                onSuccess();
            })
            .catch(e => {
                notifications.show('Error creating attribute!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">
                Create Custom Drink
            </DialogTitle>
            <FormContainer onSuccess={onSubmit} formContext={formContext}>
                <DialogContent
                    sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}
                >
                    <BusinessInput />
                    <TextFieldElement label="Name" name="name" fullWidth />
                    <TextFieldElement
                        label="Description"
                        name="description"
                        fullWidth
                    />
                    <TextFieldElement
                        label="basePrice"
                        name="basePrice"
                        fullWidth
                    />

                    <TextFieldElement
                        label="upCharge"
                        name="upCharge"
                        fullWidth
                    />
                    <CheckboxElement label="Is Alcoholic" name="isAlcoholic" />

                    <CheckboxElement label="Is Shot" name="isShot" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelCreate} color="inherit">
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        variant="contained"
                        loading={formContext.formState.isSubmitting}
                        type="submit"
                        disabled={formContext.formState.isSubmitting}
                    >
                        Create
                    </Button>
                </DialogActions>
            </FormContainer>
        </Dialog>
    );
};
